/* eslint-disable */
import React, { useState } from 'react';
import DatePicker from '../DatePicker';

// Split "YYYY-MM-DDTHH:MM" into date and time parts
const splitDT = (dt) => {
  if (!dt) return { date: '', time: '' };
  const [d, t] = dt.split('T');
  return { date: d || '', time: (t || '').slice(0, 5) };
};
// Combine date and time into "YYYY-MM-DDTHH:MM"
const combineDT = (date, time) => {
  if (!date) return '';
  return date + 'T' + (time || '00:00');
};

/**
 * CreateShiftForm — form for creating a new shift at a location.
 */
export default function CreateShiftForm({ st, setSt, account, audit, onBack, locationId: preselectedLocId }) {
  const locs = (st.locations || []).filter(l => !['In Stock', 'In Testing', 'Quarantine'].includes(l.name));

  const [locationId, setLocationId] = useState(preselectedLocId || (locs[0]?.id || ''));
  const [possessionRef, setPossessionRef] = useState('');
  const [title, setTitle] = useState('');
  const [site, setSite] = useState('');
  const [shiftNumber, setShiftNumber] = useState('');
  const [week, setWeek] = useState('');
  const [startAt, setStartAt] = useState('');
  const [endAt, setEndAt] = useState('');
  const [supervisorUserId, setSupervisorUserId] = useState(account?.name || '');
  const [anonymousMode, setAnonymousMode] = useState(false);
  const [numShifts, setNumShifts] = useState(1);
  const [shiftDurationHours, setShiftDurationHours] = useState(12);
  const [capLastShift, setCapLastShift] = useState(true);
  const [errors, setErrors] = useState({});
  const [warnings, setWarnings] = useState([]);

  // Compute how many shifts of the chosen duration fit between start and end (rounded up).
  const autoCalcShiftCount = () => {
    if (!startAt) return { valid: false, reason: 'Set start time first' };
    if (!endAt) return { valid: false, reason: 'Set end time first' };
    const startMs = new Date(startAt).getTime();
    const endMs = new Date(endAt).getTime();
    if (Number.isNaN(startMs) || Number.isNaN(endMs)) return { valid: false, reason: 'Invalid start or end time' };
    if (endMs <= startMs) return { valid: false, reason: 'End must be after start' };
    const durHours = parseFloat(shiftDurationHours) || 0;
    if (durHours <= 0) return { valid: false, reason: 'Set a shift duration' };
    const totalMs = endMs - startMs;
    const durMs = durHours * 3600000;
    const count = Math.max(1, Math.ceil(totalMs / durMs));
    const totalMin = Math.round(totalMs / 60000);
    return { valid: true, count, totalHours: Math.floor(totalMin / 60), totalMinutes: totalMin % 60 };
  };

  // Compute the planned shift windows based on start, duration, count, cap
  const plannedShifts = (() => {
    if (!startAt) return [];
    const startMs = new Date(startAt).getTime();
    if (Number.isNaN(startMs)) return [];
    const count = Math.max(1, parseInt(numShifts) || 1);
    const durMs = Math.max(0, (parseFloat(shiftDurationHours) || 0)) * 3600000;
    const endMs = endAt ? new Date(endAt).getTime() : null;
    const out = [];
    for (let i = 0; i < count; i++) {
      const s = startMs + i * durMs;
      let e = s + durMs;
      const isLast = i === count - 1;
      if (isLast && capLastShift && endMs && e > endMs) e = endMs;
      out.push({ startAt: new Date(s).toISOString(), endAt: new Date(e).toISOString() });
    }
    return out;
  })();

  const validate = () => {
    const e = {};
    if (!locationId) e.locationId = 'Required';
    if (!possessionRef.trim()) e.possessionRef = 'Required';
    if (!title.trim()) e.title = 'Required';
    if (!startAt) e.startAt = 'Required';
    if (!endAt) e.endAt = 'Required';
    if (!shiftNumber) e.shiftNumber = 'Required';
    if (!week) e.week = 'Required';
    if (!supervisorUserId.trim()) e.supervisorUserId = 'Required';
    if (startAt && endAt && new Date(endAt) <= new Date(startAt)) e.endAt = 'Must be after start';
    if ((parseInt(numShifts) || 0) < 1) e.numShifts = 'Must be at least 1';
    if ((parseInt(numShifts) || 0) > 1 && (parseFloat(shiftDurationHours) || 0) <= 0) e.shiftDurationHours = 'Required';
    setErrors(e);

    const w = [];
    if (startAt && new Date(startAt) < new Date()) w.push('Start time is in the past');
    const spanStart = plannedShifts[0]?.startAt;
    const spanEnd = plannedShifts[plannedShifts.length - 1]?.endAt;
    if (spanStart && spanEnd) {
      const overlapping = (st.shifts || []).find(s =>
        s.locationId === locationId && (s.status === 'active' || s.status === 'planned') &&
        new Date(s.startAt) < new Date(spanEnd) && new Date(s.endAt) > new Date(spanStart)
      );
      if (overlapping) w.push(`Overlaps with Shift ${overlapping.shiftNumber} (${overlapping.possessionRef})`);
    }
    if (plannedShifts.length > 1 && endAt && new Date(spanEnd) > new Date(endAt) && !capLastShift) {
      w.push(`Last shift runs past end time by ${Math.round((new Date(spanEnd) - new Date(endAt)) / 60000)} min`);
    }
    setWarnings(w);

    return Object.keys(e).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    const loc = locs.find(l => l.id === locationId);
    const now = new Date().toISOString();
    const baseShiftNumber = parseInt(shiftNumber);

    const newShifts = plannedShifts.map((p, i) => ({
      id: 'shift-' + Date.now() + '-' + i + '-' + Math.random().toString(36).slice(2, 7),
      locationId,
      possessionRef: possessionRef.trim(),
      title: title.trim(),
      site: site.trim() || loc?.name || '',
      startAt: p.startAt,
      endAt: p.endAt,
      shiftNumber: baseShiftNumber + i,
      week: parseInt(week),
      supervisorUserId: supervisorUserId.trim(),
      status: 'planned',
      anonymousMode,
      createdByUserId: account?.username || 'unknown',
      createdAt: now,
      startedAt: null,
      closedAt: null,
    }));

    setSt(prev => ({ ...prev, shifts: [...(prev.shifts || []), ...newShifts] }));

    newShifts.forEach(s => audit('shift_created', { shiftId: s.id, title: s.title, possessionRef: s.possessionRef, locationId }));
    onBack();
  };

  const selectedLoc = locs.find(l => l.id === locationId);
  const fieldStyle = { width: '100%', padding: '9px 12px', background: 'var(--surface-inset)', border: '1px solid var(--border-default)', borderRadius: 10, color: 'var(--text-default)', fontFamily: 'inherit', fontSize: 13, boxSizing: 'border-box' };
  const labelStyle = { display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 };
  const errorStyle = { fontSize: 10, color: '#f87171', marginTop: 3 };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-default)', margin: 0 }}>Create shift</h1>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>Set up a new possession / shift at a location.</div>
        </div>
      </div>

      <div className="card" style={{ padding: 22, maxWidth: 720 }}>
        <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', marginBottom: 16 }}>Possession / shift details</div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
          <div>
            <label style={labelStyle}>Location</label>
            <select value={locationId} onChange={e => setLocationId(e.target.value)} style={{ ...fieldStyle, appearance: 'none' }}>
              <option value="">Select location</option>
              {locs.map(l => <option key={l.id} value={l.id}>{l.name}{l.customer ? ' — ' + l.customer : ''}</option>)}
            </select>
            {errors.locationId && <div style={errorStyle}>{errors.locationId}</div>}
          </div>
          <div>
            <label style={labelStyle}>Client <span style={{ color: 'var(--text-subtle)', fontWeight: 400, textTransform: 'none' }}>(from location)</span></label>
            <input value={selectedLoc?.customer || ''} disabled style={{ ...fieldStyle, opacity: 0.6 }} />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
          <div>
            <label style={labelStyle}>Possession reference</label>
            <input value={possessionRef} onChange={e => setPossessionRef(e.target.value)} placeholder="e.g. P4034438" style={fieldStyle} />
            {errors.possessionRef && <div style={errorStyle}>{errors.possessionRef}</div>}
          </div>
          <div>
            <label style={labelStyle}>Site <span style={{ color: 'var(--text-subtle)', fontWeight: 400, textTransform: 'none' }}>(specific area)</span></label>
            <input value={site} onChange={e => setSite(e.target.value)} placeholder={selectedLoc?.name || 'e.g. Fratton Station'} style={fieldStyle} />
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Title / route description</label>
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Farlington Jn → Portsmouth Harbour" style={fieldStyle} />
          {errors.title && <div style={errorStyle}>{errors.title}</div>}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
          <div>
            <label style={labelStyle}>Shift number {numShifts > 1 && <span style={{ color: 'var(--text-subtle)', fontWeight: 400, textTransform: 'none' }}>(first — others auto-increment)</span>}</label>
            <input type="number" value={shiftNumber} onChange={e => setShiftNumber(e.target.value)} placeholder="11" style={fieldStyle} />
            {errors.shiftNumber && <div style={errorStyle}>{errors.shiftNumber}</div>}
          </div>
          <div>
            <label style={labelStyle}>Week</label>
            <input type="number" value={week} onChange={e => setWeek(e.target.value)} placeholder="53" style={fieldStyle} />
            {errors.week && <div style={errorStyle}>{errors.week}</div>}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
          <div>
            <label style={labelStyle}>Start</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}>
                <DatePicker
                  value={splitDT(startAt).date}
                  onChange={e => setStartAt(combineDT(e.target.value, splitDT(startAt).time))}
                />
              </div>
              <input
                type="time"
                value={splitDT(startAt).time}
                onChange={e => setStartAt(combineDT(splitDT(startAt).date, e.target.value))}
                style={{ ...fieldStyle, width: 100, flexShrink: 0 }}
              />
            </div>
            {errors.startAt && <div style={errorStyle}>{errors.startAt}</div>}
          </div>
          <div>
            <label style={labelStyle}>End</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}>
                <DatePicker
                  value={splitDT(endAt).date}
                  onChange={e => setEndAt(combineDT(e.target.value, splitDT(endAt).time))}
                />
              </div>
              <input
                type="time"
                value={splitDT(endAt).time}
                onChange={e => setEndAt(combineDT(splitDT(endAt).date, e.target.value))}
                style={{ ...fieldStyle, width: 100, flexShrink: 0 }}
              />
            </div>
            {errors.endAt && <div style={errorStyle}>{errors.endAt}</div>}
          </div>
        </div>

        {/* Multi-shift split */}
        <div style={{ marginBottom: 14, padding: '14px 16px', background: numShifts > 1 ? 'rgba(96,165,250,0.06)' : 'var(--surface-inset)', border: '1px solid ' + (numShifts > 1 ? 'rgba(96,165,250,0.3)' : 'var(--border-default)'), borderRadius: 10, transition: 'all 0.15s' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-default)', marginBottom: 4 }}>Split into multiple shifts</div>
          <div style={{ fontSize: 11, color: 'var(--text-subtle)', marginBottom: 12, lineHeight: 1.5 }}>
            Auto-create back-to-back shifts of a fixed length, starting at the start time above. Leave count at 1 to create a single shift.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>Number of shifts</label>
              <div style={{ display: 'flex', gap: 6 }}>
                <input type="number" min="1" value={numShifts} onChange={e => setNumShifts(e.target.value)} style={{ ...fieldStyle, flex: 1 }} />
                {(() => {
                  const auto = autoCalcShiftCount();
                  return (
                    <button
                      type="button"
                      onClick={() => { if (auto.valid) setNumShifts(auto.count); }}
                      disabled={!auto.valid}
                      title={auto.valid ? `Fit ${auto.count} × ${shiftDurationHours}h shifts into the start→end window` : auto.reason}
                      style={{
                        padding: '0 12px', height: 36, fontSize: 11, fontWeight: 600,
                        background: auto.valid ? 'rgba(96,165,250,0.12)' : 'var(--surface-inset)',
                        border: '1px solid ' + (auto.valid ? 'rgba(96,165,250,0.4)' : 'var(--border-default)'),
                        color: auto.valid ? '#60a5fa' : 'var(--text-subtle)',
                        borderRadius: 10, cursor: auto.valid ? 'pointer' : 'not-allowed',
                        fontFamily: 'inherit', flexShrink: 0, letterSpacing: '0.04em', textTransform: 'uppercase',
                      }}
                    >
                      Auto
                    </button>
                  );
                })()}
              </div>
              {(() => {
                const auto = autoCalcShiftCount();
                if (auto.valid) {
                  return <div style={{ fontSize: 10, color: 'var(--text-subtle)', marginTop: 4 }}>Window: {auto.totalHours}h {auto.totalMinutes}m → {auto.count} shift{auto.count === 1 ? '' : 's'}</div>;
                }
                return <div style={{ fontSize: 10, color: 'var(--text-subtle)', marginTop: 4 }}>Auto: {auto.reason}</div>;
              })()}
              {errors.numShifts && <div style={errorStyle}>{errors.numShifts}</div>}
            </div>
            <div>
              <label style={labelStyle}>Shift duration (hours)</label>
              <input type="number" min="0" step="0.5" value={shiftDurationHours} onChange={e => setShiftDurationHours(e.target.value)} style={fieldStyle} />
              {errors.shiftDurationHours && <div style={errorStyle}>{errors.shiftDurationHours}</div>}
            </div>
          </div>
          {numShifts > 1 && (
            <>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 12, color: 'var(--text-default)', cursor: 'pointer' }}>
                <input type="checkbox" checked={capLastShift} onChange={e => setCapLastShift(e.target.checked)} />
                Cap last shift to end time
              </label>
              {plannedShifts.length > 0 && (
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border-default)' }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-subtle)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>Preview</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {plannedShifts.map((p, i) => {
                      const s = new Date(p.startAt);
                      const e = new Date(p.endAt);
                      const durMin = Math.round((e - s) / 60000);
                      const fmt = (d) => d.toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
                      return (
                        <div key={i} style={{ fontSize: 11, color: 'var(--text-default)', fontVariantNumeric: 'tabular-nums' }}>
                          <strong style={{ color: 'var(--brand-default)' }}>#{(parseInt(shiftNumber) || 0) + i}</strong> {fmt(s)} → {fmt(e)} <span style={{ color: 'var(--text-subtle)' }}>({Math.floor(durMin / 60)}h {durMin % 60}m)</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Onwave Supervisor</label>
          <input value={supervisorUserId} onChange={e => setSupervisorUserId(e.target.value)} placeholder="J. Reason" style={fieldStyle} />
          {errors.supervisorUserId && <div style={errorStyle}>{errors.supervisorUserId}</div>}
        </div>

        {/* Anonymous mode toggle */}
        <div style={{ marginBottom: 14, padding: '14px 16px', background: anonymousMode ? 'rgba(191,128,255,0.08)' : 'var(--surface-inset)', border: '1px solid ' + (anonymousMode ? 'rgba(191,128,255,0.3)' : 'var(--border-default)'), borderRadius: 10, transition: 'all 0.15s' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-default)', marginBottom: 2, display: 'flex', alignItems: 'center', gap: 6 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={anonymousMode ? '#bf80ff' : 'currentColor'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                Anonymous mode
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-subtle)', lineHeight: 1.5 }}>
                When enabled, device serials are replaced with random IDs for GDPR compliance. Operative details remain visible.
              </div>
            </div>
            <button
              type="button"
              onClick={() => setAnonymousMode(v => !v)}
              style={{
                width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer', flexShrink: 0,
                background: anonymousMode ? '#bf80ff' : 'var(--surface-raised)',
                position: 'relative', transition: 'background 0.2s',
              }}
            >
              <div style={{
                width: 18, height: 18, borderRadius: '50%', background: '#fff', position: 'absolute', top: 3,
                left: anonymousMode ? 23 : 3, transition: 'left 0.2s',
                boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
              }} />
            </button>
          </div>
        </div>

        {/* Warnings */}
        {warnings.length > 0 && (
          <div style={{ background: 'rgba(251,191,36,0.12)', border: '1px solid #fbbf24', borderRadius: 10, padding: '10px 12px', fontSize: 12, color: '#fbbf24', marginBottom: 14 }}>
            {warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
          </div>
        )}

        <div style={{ background: 'rgba(96,165,250,0.12)', border: '1px solid #60a5fa', borderRadius: 10, padding: '10px 12px', fontSize: 12, color: '#60a5fa', marginTop: 12 }}>
          <strong style={{ color: 'var(--text-default)', display: 'block', marginBottom: 2 }}>Shift starts in Planned state</strong>
          It becomes Active automatically at the start time, or when the supervisor taps "Start shift" in the mobile app.
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
          <button onClick={onBack} className="btn btn-ghost">Cancel</button>
          <button onClick={handleSubmit} className="btn btn-primary" style={{ marginLeft: 'auto' }}>{plannedShifts.length > 1 ? `Create ${plannedShifts.length} shifts` : 'Create shift'}</button>
        </div>
      </div>
    </div>
  );
}
